import { createContext, useContext } from "react";
import type { Locale } from "../api/types";

/** The signed-in user's UI language (app_user.locale). Shared components read it without needing the session. */
export const LocaleContext = createContext<Locale>("en");

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/** Picks the Thai or English name of a bilingual value. */
export function pickName(locale: Locale, nameEn: string, nameTh: string | null | undefined): string {
  return locale === "th" && nameTh ? nameTh : nameEn;
}

/**
 * The app's own static UI chrome — menus, toolbars, buttons, filter labels, form help/error text — renders in
 * English only, independent of the signed-in user's stored `locale`. Product decision, 2026-09-17: an admin
 * account with locale='th' caused the Kanban board's toolbar (search placeholder, "Type", "More", "Quick
 * filters", "Group by") to render in Thai, which is not wanted for now.
 *
 * This is deliberately NOT the same thing as `useLocale()` / `session.locale`, which stay the real per-user
 * value for genuinely content-related locale choices: `pickName`-style per-record bilingual fields (a project
 * field's own `labelEn`/`labelTh`) and date/number formatting. Those must keep reading the real locale — do not
 * route them through this function. Workflow status names have their own switch, `statusLocale()` below.
 *
 * Every `*Text.ts` chrome-string dictionary module selects through `chromeLocale()` instead of the `locale`
 * argument callers pass in, so the call sites (and the `useLocale()` calls that feed them) are unchanged, and
 * the Thai dictionaries stay in the code — provisional content for a later decision, not something to delete —
 * without being reachable. To restore per-user chrome locale later, change only this function.
 *
 * Test round R10 (R10-D02, MX-AC2): on the item page, its preview and the service desk ticket (header chips, SLA
 * durations and times, Details dates, comment and history times, worklog dates) dates and durations are chrome too and
 * format through this function, so a th-locale user no longer reads "Responded in 5 นาที" or "ศ. 2 ต.ค.". Since the
 * ruling of 2026-10-04 ("no thai language") every displayed date does (`shared/lib/format.ts`), boards, lists and reports
 * included.
 */
export function chromeLocale(_locale?: Locale): Locale {
  void _locale; // intentionally unused: chrome text ignores the real locale by design (2026-09-17)
  return "en";
}

/**
 * The language workflow STATUS NAMES render in — status chips/badges, the item header, the transition dialog,
 * board cards and facets, backlog, History status lines, service desk, deployments, approvals, workload. Product
 * decision, 2026-10-01: status names render in English for every user, whatever the stored `locale`, the same as
 * the app chrome (`chromeLocale()`, 2026-09-17). A locale='th' account (and a SystemAdmin viewing as such a user)
 * saw e.g. "รอตรวจทาน" instead of "Pending Review" on chips, the transition dialog and board cards.
 *
 * Scope is status names only: a status's own `nameEn`/`nameTh`. Other bilingual record fields (field labels,
 * option labels, severities, queue labels, people's names) and date/number formatting keep the real locale.
 * The Thai names stay in the API and the seeds. To restore per-user status names, change only this function.
 */
export function statusLocale(_locale?: Locale): Locale {
  void _locale; // intentionally unused: status names ignore the real locale by design (2026-10-01)
  return "en";
}

/** The display name of a workflow status. Every status-name render goes through here (see `statusLocale()`). */
export function pickStatusName(locale: Locale, status: { nameEn: string; nameTh?: string | null }): string {
  return pickName(statusLocale(locale), status.nameEn, status.nameTh);
}

/**
 * The language a catalogued SELECT VALUE renders in when the app shows it as chrome — the ticket's Details → Severity, the
 * queue table's Severity column, the header Severity chip, the service desk queue names. Product ruling 2026-10-02 (test
 * round R11, R11-D01): English for every user, whatever the stored `locale`, like the chrome (`chromeLocale()`) and status
 * names (`statusLocale()`). A th-locale user read Details → Severity "สูง" beside the header chip "Severity High"; the value
 * is the record's code (`HIGH`), so its name is chrome, not content. Only what a person typed (summary, description,
 * comments, a migrated free-text channel) keeps its own language.
 *
 * Scope: values shown on screens, and since the ruling of 2026-10-04 ("no thai language", the Emergency Approve dialog's
 * "Verbal (Thai)") the option and choice labels inside forms too — `FieldInput` (select, checklist, disabled notes),
 * `fieldOptions` — with English only, never a Thai label in parentheses or as secondary text. Field labels go through
 * `chromeLocale()`. The Thai names stay in the API and the data. To restore per-user values, change only this.
 */
export function optionLocale(_locale?: Locale): Locale {
  void _locale; // intentionally unused: select values shown as chrome ignore the real locale (2026-10-02)
  return "en";
}

/** The display name of a catalogued select value shown as chrome (see `optionLocale()`). */
export function pickOptionName(locale: Locale, nameEn: string, nameTh: string | null | undefined): string {
  return pickName(optionLocale(locale), nameEn, nameTh);
}
